// bookiesSafety.js -- the Bookies safety writes (#1421 spec section 4 steps 5-7, plan E6).
// Single job: leave a booth (membership only, never a booth delete), report a booth or member through
// report_booth, block/unblock a member, and read the caller's own blocks. RLS and the RPC enforce every rule;
// failures come back as { ok:false, code } with one Sentry report. CC-IMPL-037: a throw with no code (network), the
// two user limits (ST023, ST024) and approval required (ST025, #1503) are recoverable -> warning; any other coded
// server refusal (RLS 42501, FK 23503, PGRST202 missing RPC, P0002/22023 from report_booth) is terminal for that
// tap -> exception (deviation from plan Task 11's "one warning", pre-Codex FC F3).
import * as Sentry from '../../lib/errorReporting';

export const REPORT_REASONS = Object.freeze([
  ['inappropriate_name', 'Inappropriate booth name'], ['harassment', 'Harassment'], ['spam', 'Spam'], ['other', 'Something else'],
]);
const REASON_KEYS = REPORT_REASONS.map(([k]) => k);
// ST025 (#1503 PR 2): a member revoked after joining is refused by report_booth: expected, so a warning.
const RECOVERABLE_CODES = new Set(['ST023', 'ST024', 'ST025']);
const warn = (msg, code) => {
  const extra = { code: code ?? null };
  if (code && !RECOVERABLE_CODES.has(code)) Sentry.captureException(new Error(msg), { tags: { function: 'bookiesSafety' }, extra });
  else Sentry.captureMessage(msg, { level: 'warning', tags: { function: 'bookiesSafety' }, extra });
};

// Membership only (spec section 4 step 5, amended by Codex r3 P1). The client never deletes the booth: "last member"
// is a board snapshot, a join can land between any client check and the delete, and the creator's delete would take
// the new member's booth with it. An empty booth is ignored by every reader (spec section 10); a race-free delete is
// the leave_booth RPC follow-up.
export async function leaveBooth(client, userId, groupId) {
  try {
    const { error } = await client.from('group_members').delete().eq('group_id', groupId).eq('user_id', userId);
    if (error) { warn('bookies: leave failed', error.code); return { ok: false }; }
    return { ok: true };
  } catch { warn('bookies: leave failed', null); return { ok: false }; }
}

export async function reportBooth(client, { groupId, userId = null, reason, note = null }) {
  if (!REASON_KEYS.includes(reason)) return { ok: false, code: '22023' };
  try {
    const { error } = await client.rpc('report_booth', { p_group_id: groupId, p_user_id: userId, p_reason: reason, p_note: note });
    if (error) { warn('bookies: report failed', error.code); return { ok: false, code: error.code || null }; }
    return { ok: true, code: null };
  } catch { warn('bookies: report failed', null); return { ok: false, code: null }; }
}

export async function setBlocked(client, blockerId, blockedId, blocked) {
  const label = blocked ? 'bookies: block failed' : 'bookies: unblock failed';
  try {
    const { error } = blocked
      ? await client.from('bookies_blocks').upsert({ blocker_id: blockerId, blocked_id: blockedId }, { onConflict: 'blocker_id,blocked_id', ignoreDuplicates: true })
      : await client.from('bookies_blocks').delete().eq('blocker_id', blockerId).eq('blocked_id', blockedId);
    if (error) { warn(label, error.code); return { ok: false, code: error.code || null }; }
    return { ok: true, code: null };
  } catch { warn(label, null); return { ok: false, code: null }; }
}

export async function fetchMyBlocks(client, userId) {
  const { data, error } = await client.from('bookies_blocks').select('blocked_id').eq('blocker_id', userId);
  if (error) throw new Error(`bookies_blocks read failed: ${error.message || 'unknown'}`);
  return new Set((data || []).map((r) => r.blocked_id));
}
